import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../../Components/Common/HelpArticleLink";
import { useProfile } from "../../../../Components/Hooks/UserHooks";
import ClientPicker from "../../../../Components/Studio/ClientPicker";
import { HELP_ARTICLE_CRM } from "../../../../helpers/helpCenter";
import usePlanFeatures from "../../../../hooks/usePlanFeatures";
import { getClientById } from "../../../../services/clientService";
import { getUsers, User as StudioUser } from "../../../../services/userService";
import {
  acceptCrmQuote,
  cloneCrmQuote,
  createCrmDeal,
  createCrmPipeline,
  createCrmQuote,
  CrmDeal,
  CrmPipeline,
  CrmPipelineTemplate,
  CrmQuote,
  CrmSource,
  duplicateCrmDeal,
  getCrmDeals,
  getCrmPipelineTemplates,
  getCrmPipelines,
  getCrmQuotes,
  sendCrmQuote,
  updateCrmDeal,
} from "../../../../services/crmService";
import {
  getServicePackages,
  ServicePackage,
} from "../../../../services/servicePackageService";
import { getServices, Service } from "../../../../services/serviceService";

type ApiClientError = { data?: string };

type DraftQuoteLine = {
  key: string;
  itemType: "package" | "service";
  packageId: number | null;
  serviceId: number | null;
  price: string;
};

const newQuoteLine = (): DraftQuoteLine => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  itemType: "package",
  packageId: null,
  serviceId: null,
  price: "",
});

const quoteStatusBadgeColor = (status: string | null | undefined): string => {
  switch (status) {
    case "accepted":
      return "success";
    case "sent":
      return "primary";
    case "draft":
      return "secondary";
    case "expired":
    case "rejected":
      return "danger";
    default:
      return "light";
  }
};

/** Merge PATCH deal into board card without dropping nested list fields. */
const mergeDealCard = (previous: CrmDeal, updated: CrmDeal): CrmDeal => ({
  ...previous,
  ...updated,
  client: updated.client ?? previous.client,
  assigned_user: updated.assigned_user ?? previous.assigned_user,
  stage: updated.stage ?? previous.stage,
  quotes_count: updated.quotes_count ?? previous.quotes_count,
  latest_quote_status: updated.latest_quote_status ?? previous.latest_quote_status,
  latest_quote_total_amount:
    updated.latest_quote_total_amount ?? previous.latest_quote_total_amount,
  latest_quote_public_url:
    updated.latest_quote_public_url ?? previous.latest_quote_public_url,
});

const CrmPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useProfile();
  const deepLinkHandled = useRef(false);
  const pendingWaClientId = useRef<number | null>(null);
  const {
    crmMaxPipelines,
    crmPipelinesCount,
    crmMaxOpenDeals,
    crmOpenDealsCount,
    refresh: refreshPlanFeatures,
  } = usePlanFeatures();

  const currentUserId = useMemo(() => {
    const raw = userProfile?.user?.id;
    const id = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [userProfile]);

  const errorMessage = (error: unknown, fallback: string): string => {
    const apiError = error as ApiClientError;
    return apiError?.data || fallback;
  };
  const [loading, setLoading] = useState(true);
  const [pipelines, setPipelines] = useState<CrmPipeline[]>([]);
  const [templates, setTemplates] = useState<CrmPipelineTemplate[]>([]);
  const [activePipelineId, setActivePipelineId] = useState<number | null>(null);
  const [deals, setDeals] = useState<CrmDeal[]>([]);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [dealSearch, setDealSearch] = useState("");
  const [overdueCount, setOverdueCount] = useState(0);
  const [teamUsers, setTeamUsers] = useState<StudioUser[]>([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [dealModalOpen, setDealModalOpen] = useState(false);
  const [dealTitle, setDealTitle] = useState("");
  const [dealValue, setDealValue] = useState("");
  const [dealDueAt, setDealDueAt] = useState("");
  const [dealClientId, setDealClientId] = useState("");
  const [dealAssigneeId, setDealAssigneeId] = useState("");
  const [dealSource, setDealSource] = useState<CrmSource>("manual");
  const [dealStageId, setDealStageId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [draggingDealId, setDraggingDealId] = useState<number | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<CrmDeal | null>(null);
  const [detailClientId, setDetailClientId] = useState("");
  const [detailAssigneeId, setDetailAssigneeId] = useState("");
  const [detailNotes, setDetailNotes] = useState("");
  const [quotes, setQuotes] = useState<CrmQuote[]>([]);
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [quoteLines, setQuoteLines] = useState<DraftQuoteLine[]>([newQuoteLine()]);
  const [pendingWaCreate, setPendingWaCreate] = useState(false);

  const atPipelineLimit =
    crmMaxPipelines != null &&
    (crmPipelinesCount || pipelines.length) >= crmMaxPipelines;
  const atDealLimit =
    crmMaxOpenDeals != null && crmOpenDealsCount >= crmMaxOpenDeals;
  const nearDealLimit =
    crmMaxOpenDeals != null &&
    crmOpenDealsCount >= Math.max(1, crmMaxOpenDeals - 3) &&
    !atDealLimit;
  const [sendResult, setSendResult] = useState<{
    public_url: string;
    copy_text: string;
  } | null>(null);
  const [acceptResult, setAcceptResult] = useState<{
    orderId: number;
    clientId: number | null;
  } | null>(null);

  const activePipeline = useMemo(
    () => pipelines.find((item) => item.id === activePipelineId) ?? null,
    [pipelines, activePipelineId]
  );

  const stages = useMemo(
    () => [...(activePipeline?.stages ?? [])].sort((a, b) => a.sort_order - b.sort_order),
    [activePipeline]
  );

  const loadBoard = useCallback(
    async (pipelineId: number, overdue: boolean, mine: boolean, search: string) => {
      const assigneeId = mine && currentUserId != null ? currentUserId : undefined;
      const result = await getCrmDeals(pipelineId, {
        overdue,
        assigned_user_id: assigneeId,
        search: search || undefined,
      });
      setDeals(result.data);

      if (overdue) {
        setOverdueCount(result.meta?.total ?? result.data.length);
        return;
      }

      const overdueResult = await getCrmDeals(pipelineId, {
        overdue: true,
        assigned_user_id: assigneeId,
        search: search || undefined,
      });
      setOverdueCount(overdueResult.meta?.total ?? overdueResult.data.length);
    },
    [currentUserId]
  );

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const [pipelineList, templateList] = await Promise.all([
        getCrmPipelines(),
        getCrmPipelineTemplates(),
      ]);
      setPipelines(pipelineList);
      setTemplates(templateList);
      try {
        const usersResponse = await getUsers({ per_page: 100 });
        setTeamUsers(usersResponse.data ?? []);
      } catch {
        setTeamUsers([]);
      }

      if (pipelineList.length === 0) {
        setWizardOpen(true);
        setActivePipelineId(null);
        setDeals([]);
        setOverdueCount(0);
      } else {
        setActivePipelineId((prev) => prev ?? pipelineList[0].id);
      }
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.load")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    if (loading || pipelines.length === 0) {
      return;
    }
    const pipelineParam = Number(searchParams.get("pipeline_id"));
    if (
      Number.isFinite(pipelineParam) &&
      pipelines.some((pipeline) => pipeline.id === pipelineParam)
    ) {
      setActivePipelineId(pipelineParam);
    }
  }, [loading, pipelines, searchParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDealSearch(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (activePipelineId == null || loading) {
      return;
    }
    void loadBoard(activePipelineId, overdueOnly, mineOnly, dealSearch).catch((error) => {
      toast.error(errorMessage(error, t("crm.errors.loadDeals")));
    });
  }, [activePipelineId, overdueOnly, mineOnly, dealSearch, loadBoard, loading, t]);

  const applyClientPrefill = useCallback(
    async (clientId: number, source: CrmSource) => {
      setDealClientId(String(clientId));
      setDealSource(source);
      try {
        const response = await getClientById(clientId);
        const name = response.data?.name?.trim();
        if (name) {
          setDealTitle((prev) => (prev.trim() ? prev : name));
        }
      } catch {
        // keep modal usable without blocking if client fetch fails
      }
    },
    []
  );

  const openDealModal = useCallback(
    (stageId?: number, clientId?: number, source: CrmSource = "manual") => {
      setDealTitle("");
      setDealValue("");
      setDealDueAt("");
      setDealClientId("");
      setDealAssigneeId(currentUserId != null ? String(currentUserId) : "");
      setDealSource(source);
      setDealStageId(
        stageId ?? stages.find((stage) => !stage.is_won && !stage.is_lost)?.id ?? null
      );
      setDealModalOpen(true);
      if (clientId != null && Number.isFinite(clientId)) {
        void applyClientPrefill(clientId, source);
      }
    },
    [stages, applyClientPrefill, currentUserId]
  );

  useEffect(() => {
    if (loading || deepLinkHandled.current) {
      return;
    }
    if (searchParams.get("create") !== "1") {
      return;
    }

    if (pipelines.length === 0) {
      deepLinkHandled.current = true;
      const clientIdParam = searchParams.get("client_id");
      const clientId = clientIdParam ? Number(clientIdParam) : NaN;
      pendingWaClientId.current = Number.isFinite(clientId) ? clientId : null;
      setPendingWaCreate(true);
      setWizardOpen(true);
      toast.info(t("crm.deepLink.needPipeline"));
      setSearchParams({}, { replace: true });
      return;
    }

    const sales =
      pipelines.find((pipeline) => pipeline.purpose === "sales") ?? pipelines[0];
    if (activePipelineId !== sales.id) {
      setActivePipelineId(sales.id);
      return;
    }
    if (stages.length === 0) {
      return;
    }

    deepLinkHandled.current = true;
    const clientIdParam = searchParams.get("client_id");
    const clientId = clientIdParam ? Number(clientIdParam) : NaN;
    openDealModal(
      undefined,
      Number.isFinite(clientId) ? clientId : undefined,
      "whatsapp"
    );
    setSearchParams({}, { replace: true });
  }, [
    loading,
    pipelines,
    activePipelineId,
    stages,
    searchParams,
    setSearchParams,
    openDealModal,
    t,
  ]);

  const handleCreateFromTemplate = async (slug: string) => {
    if (atPipelineLimit) {
      toast.error(t("crm.limits.pipelineLocked"));
      return;
    }
    setSaving(true);
    try {
      const pipeline = await createCrmPipeline({ template_slug: slug });
      setPipelines((prev) => [...prev, pipeline]);
      setActivePipelineId(pipeline.id);
      setWizardOpen(false);
      toast.success(t("crm.toasts.pipelineCreated"));
      await refreshPlanFeatures();
      if (pendingWaCreate) {
        setPendingWaCreate(false);
        const clientId = pendingWaClientId.current ?? undefined;
        pendingWaClientId.current = null;
        openDealModal(undefined, clientId, "whatsapp");
      }
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.createPipeline")));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateDeal = async (event: React.FormEvent) => {
    event.preventDefault();
    if (activePipelineId == null || dealStageId == null || !dealTitle.trim()) {
      return;
    }
    if (atDealLimit) {
      toast.error(t("crm.limits.dealLocked"));
      return;
    }

    setSaving(true);
    try {
      const cents = dealValue.trim()
        ? Math.round(Number(dealValue.replace(",", ".")) * 100)
        : null;
      const clientId = dealClientId ? Number(dealClientId) : null;
      const assigneeId = dealAssigneeId ? Number(dealAssigneeId) : null;
      await createCrmDeal(activePipelineId, {
        title: dealTitle.trim(),
        stage_id: dealStageId,
        client_id: clientId && Number.isFinite(clientId) ? clientId : null,
        assigned_user_id: assigneeId && Number.isFinite(assigneeId) ? assigneeId : null,
        source: dealSource,
        estimated_value: Number.isFinite(cents as number) ? cents : null,
        due_at: dealDueAt ? new Date(`${dealDueAt}T12:00:00Z`).toISOString() : null,
      });
      setDealModalOpen(false);
      toast.success(t("crm.toasts.dealCreated"));
      await loadBoard(activePipelineId, overdueOnly, mineOnly, dealSearch);
      await refreshPlanFeatures();
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.createDeal")));
    } finally {
      setSaving(false);
    }
  };

  const openDealDetail = async (deal: CrmDeal) => {
    setSelectedDeal(deal);
    setDetailClientId(deal.client_id != null ? String(deal.client_id) : "");
    setDetailAssigneeId(
      deal.assigned_user_id != null ? String(deal.assigned_user_id) : ""
    );
    setDetailNotes(deal.notes ?? "");
    setSendResult(null);
    setAcceptResult(null);
    setQuoteLines([newQuoteLine()]);
    try {
      const [quoteList, packageResponse, serviceResponse] = await Promise.all([
        getCrmQuotes(deal.id),
        getServicePackages({ per_page: 100, is_active: true }),
        getServices({ per_page: 100 }),
      ]);
      setQuotes(quoteList);
      setPackages(packageResponse.data ?? []);
      setServices(serviceResponse.data ?? []);
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.loadQuotes")));
    }
  };

  const handleLinkClient = async (clientId: string) => {
    setDetailClientId(clientId);
    if (!selectedDeal || !clientId) {
      return;
    }
    const id = Number(clientId);
    if (!Number.isFinite(id)) {
      return;
    }
    setSaving(true);
    try {
      const updated = await updateCrmDeal(selectedDeal.id, { client_id: id });
      setSelectedDeal(updated);
      setDeals((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      toast.success(t("crm.toasts.clientLinked"));
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.linkClient")));
    } finally {
      setSaving(false);
    }
  };

  const handleAssignUser = async (userId: string) => {
    setDetailAssigneeId(userId);
    if (!selectedDeal) {
      return;
    }
    const id = userId ? Number(userId) : null;
    if (userId && !Number.isFinite(id as number)) {
      return;
    }
    setSaving(true);
    try {
      const updated = await updateCrmDeal(selectedDeal.id, {
        assigned_user_id: id,
      });
      setSelectedDeal(updated);
      setDeals((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      toast.success(t("crm.toasts.assigneeSaved"));
      if (activePipelineId != null) {
        await loadBoard(activePipelineId, overdueOnly, mineOnly, dealSearch);
      }
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.assignUser")));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedDeal) {
      return;
    }
    setSaving(true);
    try {
      const updated = await updateCrmDeal(selectedDeal.id, {
        notes: detailNotes.trim() || null,
      });
      setSelectedDeal(updated);
      setDeals((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      toast.success(t("crm.toasts.notesSaved"));
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.saveNotes")));
    } finally {
      setSaving(false);
    }
  };

  const copyQuoteLink = (url: string, event?: React.MouseEvent) => {
    event?.stopPropagation();
    void navigator.clipboard.writeText(url).then(
      () => toast.success(t("crm.quote.linkCopied")),
      () => toast.error(t("crm.errors.copyLink"))
    );
  };

  const handleSnoozeDue = async (days: number) => {
    if (!selectedDeal) {
      return;
    }
    const base =
      selectedDeal.due_at && new Date(selectedDeal.due_at).getTime() > Date.now()
        ? new Date(selectedDeal.due_at)
        : new Date();
    base.setUTCDate(base.getUTCDate() + days);
    base.setUTCHours(12, 0, 0, 0);
    setSaving(true);
    try {
      const updated = await updateCrmDeal(selectedDeal.id, {
        due_at: base.toISOString(),
      });
      setSelectedDeal(updated);
      setDeals((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      toast.success(t("crm.toasts.dueSnoozed", { days }));
      if (activePipelineId != null) {
        await loadBoard(activePipelineId, overdueOnly, mineOnly, dealSearch);
      }
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.snoozeDue")));
    } finally {
      setSaving(false);
    }
  };

  const updateQuoteLine = (key: string, patch: Partial<DraftQuoteLine>) => {
    setQuoteLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, ...patch } : line))
    );
  };

  const handleCreateQuote = async () => {
    if (!selectedDeal) {
      return;
    }
    const clientId = selectedDeal.client_id;
    if (!clientId) {
      toast.error(t("crm.quote.needClient"));
      return;
    }

    const items: Array<{
      item_type: "package" | "service";
      service_package_id?: number;
      service_id?: number;
      quantity: number;
      unit_price: number;
    }> = [];

    for (const line of quoteLines) {
      const cents = Math.round(Number(line.price.replace(",", ".")) * 100);
      if (!Number.isFinite(cents) || cents < 0) {
        toast.error(t("crm.errors.quotePrice"));
        return;
      }
      if (line.itemType === "package" && line.packageId) {
        items.push({
          item_type: "package",
          service_package_id: line.packageId,
          quantity: 1,
          unit_price: cents,
        });
      } else if (line.itemType === "service" && line.serviceId) {
        items.push({
          item_type: "service",
          service_id: line.serviceId,
          quantity: 1,
          unit_price: cents,
        });
      }
    }

    if (items.length === 0) {
      toast.error(t("crm.errors.quoteLines"));
      return;
    }

    setSaving(true);
    try {
      const quote = await createCrmQuote(selectedDeal.id, {
        client_id: clientId,
        items,
      });
      setQuotes((prev) => [quote, ...prev]);
      setQuoteLines([newQuoteLine()]);
      toast.success(t("crm.toasts.quoteCreated"));
      if (activePipelineId != null) {
        await loadBoard(activePipelineId, overdueOnly, mineOnly, dealSearch);
      }
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.createQuote")));
    } finally {
      setSaving(false);
    }
  };

  const handleSendQuote = async (quoteId: number) => {
    setSaving(true);
    try {
      const result = await sendCrmQuote(quoteId, true);
      setQuotes((prev) =>
        prev.map((item) => (item.id === result.quote.id ? result.quote : item))
      );
      setSendResult({
        public_url: result.public_url,
        copy_text: result.copy_text,
      });
      toast.success(t("crm.toasts.quoteSent"));
      if (activePipelineId != null) {
        await loadBoard(activePipelineId, overdueOnly, mineOnly, dealSearch);
      }
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.sendQuote")));
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptQuote = async (quoteId: number) => {
    setSaving(true);
    try {
      const result = await acceptCrmQuote(quoteId);
      setQuotes((prev) =>
        prev.map((item) => (item.id === result.quote.id ? result.quote : item))
      );
      setAcceptResult({
        orderId: result.order_id,
        clientId: result.client_id ?? selectedDeal?.client_id ?? null,
      });
      toast.success(t("crm.toasts.quoteAccepted"));
      if (activePipelineId != null) {
        await loadBoard(activePipelineId, overdueOnly, mineOnly, dealSearch);
      }
      await refreshPlanFeatures();
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.acceptQuote")));
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateDeal = async () => {
    if (!selectedDeal || activePipelineId == null) {
      return;
    }
    setSaving(true);
    try {
      const result = await duplicateCrmDeal(selectedDeal.id, true);
      toast.success(t("crm.toasts.dealDuplicated"));
      await loadBoard(activePipelineId, overdueOnly, mineOnly, dealSearch);
      await refreshPlanFeatures();
      await openDealDetail(result.deal);
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.duplicateDeal")));
    } finally {
      setSaving(false);
    }
  };

  const handleCloneQuote = async (quoteId: number) => {
    setSaving(true);
    try {
      const cloned = await cloneCrmQuote(quoteId);
      setQuotes((prev) => [cloned, ...prev]);
      toast.success(t("crm.toasts.quoteCloned"));
      if (activePipelineId != null) {
        await loadBoard(activePipelineId, overdueOnly, mineOnly, dealSearch);
      }
    } catch (error) {
      toast.error(errorMessage(error, t("crm.errors.cloneQuote")));
    } finally {
      setSaving(false);
    }
  };

  const isOverdue = (deal: CrmDeal): boolean => {
    if (!deal.due_at || deal.won_at || deal.lost_at) {
      return false;
    }
    return new Date(deal.due_at).getTime() < Date.now();
  };

  const handleDropOnStage = async (stageId: number) => {
    if (draggingDealId == null || activePipelineId == null) {
      return;
    }
    const deal = deals.find((item) => item.id === draggingDealId);
    if (!deal || deal.stage_id === stageId) {
      setDraggingDealId(null);
      return;
    }

    const targetStage = stages.find((stage) => stage.id === stageId);
    let lostReason: string | null = null;
    if (targetStage?.is_lost) {
      const answer = window.prompt(t("crm.lostReason.prompt"), "");
      if (answer === null) {
        setDraggingDealId(null);
        return;
      }
      lostReason = answer.trim() || t("crm.lostReason.default");
    }

    const snapshot = deals;
    const wasOverdue = isOverdue(deal);
    const becameTerminal = Boolean(targetStage?.is_won || targetStage?.is_lost);
    const becameOpenAgain = !becameTerminal && Boolean(deal.won_at || deal.lost_at);
    const optimistic: CrmDeal = {
      ...deal,
      stage_id: stageId,
      stage: targetStage ?? deal.stage,
      won_at: targetStage?.is_won ? new Date().toISOString() : null,
      lost_at: targetStage?.is_lost ? new Date().toISOString() : null,
      lost_reason: targetStage?.is_lost ? lostReason : null,
    };
    const optimisticOverdue = isOverdue(optimistic);

    setDeals((prev) =>
      prev.map((item) => (item.id === deal.id ? optimistic : item))
    );
    setDraggingDealId(null);

    if (wasOverdue && becameTerminal) {
      setOverdueCount((count) => Math.max(0, count - 1));
    } else if (becameOpenAgain && optimisticOverdue) {
      setOverdueCount((count) => count + 1);
    }

    try {
      const updated = await updateCrmDeal(deal.id, {
        stage_id: stageId,
        lost_reason: lostReason,
      });
      setDeals((prev) =>
        prev.map((item) =>
          item.id === updated.id ? mergeDealCard(item, updated) : item
        )
      );
      toast.success(t("crm.toasts.dealMoved"));
      void refreshPlanFeatures();
    } catch (error) {
      setDeals(snapshot);
      if (wasOverdue && becameTerminal) {
        setOverdueCount((count) => count + 1);
      } else if (becameOpenAgain && optimisticOverdue) {
        setOverdueCount((count) => Math.max(0, count - 1));
      }
      toast.error(errorMessage(error, t("crm.errors.moveDeal")));
    }
  };

  const formatMoney = (cents: number | null): string => {
    if (cents == null) {
      return "—";
    }
    return (cents / 100).toLocaleString(undefined, {
      style: "currency",
      currency: "BRL",
    });
  };

  const limitsLabel = t("crm.limits", {
    pipelines: crmPipelinesCount || pipelines.length,
    maxPipelines: crmMaxPipelines ?? "—",
    deals: crmOpenDealsCount,
    maxDeals: crmMaxOpenDeals ?? "—",
  });

  const boardStats = useMemo(() => {
    const open = deals.filter((deal) => !deal.won_at && !deal.lost_at).length;
    const overdue = deals.filter((deal) => isOverdue(deal)).length;
    const quoteSent = deals.filter(
      (deal) => deal.latest_quote_status === "sent" || deal.latest_quote_status === "accepted"
    ).length;
    const won = deals.filter((deal) => Boolean(deal.won_at)).length;
    return { open, overdue, quoteSent, won };
  }, [deals]);

  document.title = t("crm.pageTitle");

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("crm.title")} pageTitle={t("crm.title")} />
        <div className="d-flex justify-content-end mb-3">
          <HelpArticleLink articlePath={HELP_ARTICLE_CRM} />
        </div>
        <ToastContainer autoClose={3000} />

        <Row className="mb-3 align-items-center g-2">
          <Col md={3}>
            <Input
              type="select"
              value={activePipelineId ?? ""}
              onChange={(event) => setActivePipelineId(Number(event.target.value))}
              disabled={pipelines.length === 0}
            >
              {pipelines.length === 0 && (
                <option value="">{t("crm.empty.noPipeline")}</option>
              )}
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </option>
              ))}
            </Input>
          </Col>
          <Col md={3}>
            <Input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t("crm.filters.searchPlaceholder")}
              disabled={!activePipeline}
            />
          </Col>
          <Col md="auto" className="d-flex flex-wrap align-items-center gap-3">
            <FormGroup check className="mb-0">
              <Input
                id="crm-overdue"
                type="checkbox"
                checked={overdueOnly}
                onChange={(event) => setOverdueOnly(event.target.checked)}
              />
              <Label check htmlFor="crm-overdue" className="ms-1">
                {t("crm.filters.overdue")}
                {overdueCount > 0 && (
                  <Badge color="danger" className="ms-1 align-middle">
                    {overdueCount}
                  </Badge>
                )}
              </Label>
            </FormGroup>
            <FormGroup check className="mb-0">
              <Input
                id="crm-mine"
                type="checkbox"
                checked={mineOnly}
                disabled={currentUserId == null}
                onChange={(event) => setMineOnly(event.target.checked)}
              />
              <Label check htmlFor="crm-mine" className="ms-1">
                {t("crm.filters.mine")}
              </Label>
            </FormGroup>
          </Col>
          <Col md="auto" className="ms-md-auto d-flex gap-2">
            <Button
              color="light"
              disabled={atPipelineLimit}
              onClick={() => setWizardOpen(true)}
            >
              {t("crm.actions.newPipeline")}
            </Button>
            <Button
              color="primary"
              disabled={!activePipeline || atDealLimit}
              onClick={() => openDealModal()}
            >
              {t("crm.actions.newDeal")}
            </Button>
          </Col>
        </Row>

        <p className="text-muted small mb-2">{limitsLabel}</p>
        {activePipeline && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Badge color="secondary" pill>
              {t("crm.stats.open", { count: boardStats.open })}
            </Badge>
            <Badge color={boardStats.overdue > 0 ? "danger" : "light"} pill>
              {t("crm.stats.overdue", { count: boardStats.overdue })}
            </Badge>
            <Badge color="primary" pill>
              {t("crm.stats.quoteSent", { count: boardStats.quoteSent })}
            </Badge>
            <Badge color="success" pill>
              {t("crm.stats.won", { count: boardStats.won })}
            </Badge>
          </div>
        )}
        {(atDealLimit || atPipelineLimit || nearDealLimit) && (
          <Alert color={atDealLimit || atPipelineLimit ? "warning" : "info"} className="py-2">
            {atDealLimit && <div>{t("crm.limits.dealLocked")}</div>}
            {atPipelineLimit && <div>{t("crm.limits.pipelineLocked")}</div>}
            {nearDealLimit && !atDealLimit && (
              <div>{t("crm.limits.dealNear", { remaining: (crmMaxOpenDeals ?? 0) - crmOpenDealsCount })}</div>
            )}
            <Link to="/settings/subscription" className="alert-link">
              {t("crm.limits.upgradeCta")}
            </Link>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : !activePipeline ? (
          <div className="text-center py-5 border rounded bg-white">
            <h5>{t("crm.empty.title")}</h5>
            <p className="text-muted">{t("crm.empty.subtitle")}</p>
            <Button color="primary" onClick={() => setWizardOpen(true)}>
              {t("crm.actions.startWizard")}
            </Button>
          </div>
        ) : (
          <div className="d-flex gap-3 overflow-auto pb-3" style={{ minHeight: 420 }}>
            {stages.map((stage) => {
              const stageDeals = deals.filter((deal) => deal.stage_id === stage.id);
              return (
                <div
                  key={stage.id}
                  className="bg-light border rounded p-2 flex-shrink-0"
                  style={{ width: 280 }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => void handleDropOnStage(stage.id)}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>{stage.name}</strong>
                    <Badge color="secondary">{stageDeals.length}</Badge>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {stageDeals.length === 0 && (
                      <div className="text-muted small p-2">{t("crm.empty.column")}</div>
                    )}
                    {stageDeals.map((deal) => (
                      <div
                        key={deal.id}
                        className="bg-white border rounded p-2 shadow-sm"
                        draggable
                        onDragStart={() => setDraggingDealId(deal.id)}
                        onDragEnd={() => setDraggingDealId(null)}
                        onClick={() => void openDealDetail(deal)}
                        style={{ cursor: "pointer" }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            void openDealDetail(deal);
                          }
                        }}
                      >
                        <div className="fw-semibold">{deal.title}</div>
                        {deal.client?.name && (
                          <div className="small text-muted">{deal.client.name}</div>
                        )}
                        {deal.assigned_user?.name && (
                          <div className="small text-muted">
                            {t("crm.card.assignee", { name: deal.assigned_user.name })}
                          </div>
                        )}
                        <div className="small text-muted">
                          {formatMoney(deal.estimated_value)}
                        </div>
                        {deal.latest_quote_total_amount != null && (
                          <div
                            className="small text-muted"
                            title={t("crm.card.quotePreviewTitle", {
                              status: t(
                                `crm.quoteStatus.${deal.latest_quote_status || "draft"}`,
                                { defaultValue: deal.latest_quote_status || "draft" }
                              ),
                              total: formatMoney(deal.latest_quote_total_amount),
                            })}
                          >
                            {t("crm.card.quotePreview", {
                              total: formatMoney(deal.latest_quote_total_amount),
                            })}
                          </div>
                        )}
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {deal.due_at && (
                            <Badge color={isOverdue(deal) ? "danger" : "info"}>
                              {t("crm.card.due", {
                                date: new Date(deal.due_at).toLocaleDateString(),
                              })}
                            </Badge>
                          )}
                          {(deal.quotes_count ?? 0) > 0 && (
                            <Badge
                              color={quoteStatusBadgeColor(deal.latest_quote_status)}
                            >
                              {t("crm.card.quote", {
                                status: t(
                                  `crm.quoteStatus.${deal.latest_quote_status || "draft"}`,
                                  {
                                    defaultValue: deal.latest_quote_status || "draft",
                                  }
                                ),
                              })}
                            </Badge>
                          )}
                        </div>
                        {deal.latest_quote_public_url && (
                          <Button
                            size="sm"
                            color="link"
                            className="px-0 mt-1"
                            type="button"
                            onClick={(event) =>
                              copyQuoteLink(deal.latest_quote_public_url as string, event)
                            }
                          >
                            {t("crm.card.copyLink")}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  {!stage.is_won && !stage.is_lost && (
                    <Button
                      color="link"
                      size="sm"
                      className="mt-2 px-0"
                      disabled={atDealLimit}
                      onClick={() => openDealModal(stage.id)}
                    >
                      {t("crm.actions.addInColumn")}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Container>

      <Modal isOpen={wizardOpen} toggle={() => setWizardOpen(false)} centered>
        <ModalHeader toggle={() => setWizardOpen(false)}>
          {t("crm.wizard.title")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">{t("crm.wizard.subtitle")}</p>
          <div className="d-flex flex-column gap-2">
            {templates.map((template) => (
              <Button
                key={template.slug}
                color="light"
                className="text-start"
                disabled={saving}
                onClick={() => void handleCreateFromTemplate(template.slug)}
              >
                <div className="fw-semibold">{template.name}</div>
                <div className="small text-muted">
                  {t(`crm.purpose.${template.purpose}`)}
                </div>
              </Button>
            ))}
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={dealModalOpen} toggle={() => setDealModalOpen(false)} centered>
        <Form onSubmit={(event) => void handleCreateDeal(event)}>
          <ModalHeader toggle={() => setDealModalOpen(false)}>
            {t("crm.dealModal.title")}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>{t("crm.dealModal.titleField")}</Label>
              <Input
                value={dealTitle}
                onChange={(event) => setDealTitle(event.target.value)}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("crm.dealModal.client")}</Label>
              <ClientPicker
                value={dealClientId}
                onChange={(value) => {
                  setDealClientId(value);
                  const id = Number(value);
                  if (Number.isFinite(id) && id > 0) {
                    void applyClientPrefill(id, dealSource);
                  }
                }}
              />
              <div className="form-text">{t("crm.dealModal.clientHint")}</div>
            </FormGroup>
            <FormGroup>
              <Label>{t("crm.dealModal.value")}</Label>
              <Input
                value={dealValue}
                onChange={(event) => setDealValue(event.target.value)}
                placeholder="0,00"
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("crm.dealModal.due")}</Label>
              <Input
                type="date"
                value={dealDueAt}
                onChange={(event) => setDealDueAt(event.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("crm.dealModal.assignee")}</Label>
              <Input
                type="select"
                value={dealAssigneeId}
                onChange={(event) => setDealAssigneeId(event.target.value)}
              >
                <option value="">{t("crm.dealModal.assigneeNone")}</option>
                {teamUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>{t("crm.dealModal.stage")}</Label>
              <Input
                type="select"
                value={dealStageId ?? ""}
                onChange={(event) => setDealStageId(Number(event.target.value))}
              >
                {stages
                  .filter((stage) => !stage.is_won && !stage.is_lost)
                  .map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
              </Input>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={() => setDealModalOpen(false)}>
              {t("crm.actions.cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" /> : t("crm.actions.save")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        isOpen={selectedDeal != null}
        toggle={() => setSelectedDeal(null)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setSelectedDeal(null)}>
          {selectedDeal?.title}
        </ModalHeader>
        <ModalBody>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button
              size="sm"
              color="light"
              type="button"
              disabled={saving}
              onClick={() => void handleDuplicateDeal()}
            >
              {t("crm.actions.duplicateDeal")}
            </Button>
          </div>
          <FormGroup>
            <Label>{t("crm.dealModal.client")}</Label>
            <ClientPicker
              value={detailClientId}
              onChange={(value) => void handleLinkClient(value)}
              isDisabled={saving}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("crm.dealModal.assignee")}</Label>
            <Input
              type="select"
              value={detailAssigneeId}
              disabled={saving}
              onChange={(event) => void handleAssignUser(event.target.value)}
            >
              <option value="">{t("crm.dealModal.assigneeNone")}</option>
              {teamUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Input>
          </FormGroup>
          {selectedDeal && !selectedDeal.won_at && !selectedDeal.lost_at && (
            <FormGroup>
              <Label>{t("crm.snooze.label")}</Label>
              <div className="d-flex flex-wrap gap-2">
                {[1, 3, 7].map((days) => (
                  <Button
                    key={days}
                    size="sm"
                    color="light"
                    type="button"
                    disabled={saving}
                    onClick={() => void handleSnoozeDue(days)}
                  >
                    {t("crm.snooze.days", { days })}
                  </Button>
                ))}
              </div>
              <div className="form-text">{t("crm.snooze.hint")}</div>
            </FormGroup>
          )}

          <FormGroup>
            <Label>{t("crm.notes.label")}</Label>
            <Input
              type="textarea"
              rows={3}
              value={detailNotes}
              onChange={(event) => setDetailNotes(event.target.value)}
              placeholder={t("crm.notes.placeholder")}
            />
            <Button
              size="sm"
              color="light"
              className="mt-2"
              type="button"
              disabled={saving}
              onClick={() => void handleSaveNotes()}
            >
              {t("crm.notes.save")}
            </Button>
          </FormGroup>

          {activePipeline?.purpose !== "sales" ? (
            <p className="text-muted mb-0">{t("crm.quote.salesOnly")}</p>
          ) : (
            <>
              <p className="text-muted small">{t("crm.quote.hint")}</p>

              {quoteLines.map((line, index) => (
                <div key={line.key} className="border rounded p-2 mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong className="small">
                      {t("crm.quote.lineLabel", { n: index + 1 })}
                    </strong>
                    {quoteLines.length > 1 && (
                      <Button
                        color="link"
                        size="sm"
                        className="text-danger px-0"
                        type="button"
                        onClick={() =>
                          setQuoteLines((prev) =>
                            prev.filter((item) => item.key !== line.key)
                          )
                        }
                      >
                        {t("crm.quote.removeLine")}
                      </Button>
                    )}
                  </div>
                  <FormGroup className="mb-2">
                    <Label>{t("crm.quote.itemType")}</Label>
                    <Input
                      type="select"
                      value={line.itemType}
                      onChange={(event) =>
                        updateQuoteLine(line.key, {
                          itemType: event.target.value as "package" | "service",
                          packageId: null,
                          serviceId: null,
                          price: "",
                        })
                      }
                    >
                      <option value="package">{t("crm.quote.package")}</option>
                      <option value="service">{t("crm.quote.service")}</option>
                    </Input>
                  </FormGroup>
                  {line.itemType === "package" ? (
                    <FormGroup className="mb-2">
                      <Label>{t("crm.quote.package")}</Label>
                      <Input
                        type="select"
                        value={line.packageId ?? ""}
                        onChange={(event) => {
                          const id = Number(event.target.value);
                          const pkg = packages.find((item) => item.id === id);
                          updateQuoteLine(line.key, {
                            packageId: id || null,
                            price: pkg
                              ? (pkg.price / 100).toFixed(2).replace(".", ",")
                              : line.price,
                          });
                        }}
                      >
                        <option value="">{t("crm.quote.pickPackage")}</option>
                        {packages.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {pkg.name}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  ) : (
                    <FormGroup className="mb-2">
                      <Label>{t("crm.quote.service")}</Label>
                      <Input
                        type="select"
                        value={line.serviceId ?? ""}
                        onChange={(event) => {
                          const id = Number(event.target.value);
                          const service = services.find((item) => item.id === id);
                          updateQuoteLine(line.key, {
                            serviceId: id || null,
                            price: service
                              ? (service.price / 100).toFixed(2).replace(".", ",")
                              : line.price,
                          });
                        }}
                      >
                        <option value="">{t("crm.quote.pickService")}</option>
                        {services.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  )}
                  <FormGroup className="mb-0">
                    <Label>{t("crm.quote.price")}</Label>
                    <Input
                      value={line.price}
                      onChange={(event) =>
                        updateQuoteLine(line.key, { price: event.target.value })
                      }
                      placeholder="0,00"
                    />
                  </FormGroup>
                </div>
              ))}

              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button
                  color="light"
                  size="sm"
                  type="button"
                  onClick={() => setQuoteLines((prev) => [...prev, newQuoteLine()])}
                >
                  {t("crm.quote.addLine")}
                </Button>
                <Button
                  color="primary"
                  disabled={saving || !selectedDeal?.client_id}
                  onClick={() => void handleCreateQuote()}
                >
                  {t("crm.quote.create")}
                </Button>
                {!selectedDeal?.client_id && (
                  <span className="small text-warning align-self-center">
                    {t("crm.quote.needClient")}
                  </span>
                )}
              </div>

              {quotes.length > 0 && (
                <div className="border rounded p-2">
                  <strong className="d-block mb-2">{t("crm.quote.listTitle")}</strong>
                  {quotes.map((quote) => (
                    <div
                      key={quote.id}
                      className="d-flex flex-wrap align-items-center gap-2 mb-2 pb-2 border-bottom"
                    >
                      <span>
                        #{quote.id} ·{" "}
                        {t(`crm.quoteStatus.${quote.status}`, {
                          defaultValue: quote.status,
                        })}{" "}
                        · {formatMoney(quote.total_amount)}
                      </span>
                      {quote.status !== "accepted" && (
                        <>
                          <Button
                            size="sm"
                            color="light"
                            disabled={saving}
                            onClick={() => void handleSendQuote(quote.id)}
                          >
                            {t("crm.quote.send")}
                          </Button>
                          <Button
                            size="sm"
                            color="success"
                            disabled={saving}
                            onClick={() => void handleAcceptQuote(quote.id)}
                          >
                            {t("crm.quote.accept")}
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        color="link"
                        className="px-0"
                        disabled={saving}
                        onClick={() => void handleCloneQuote(quote.id)}
                      >
                        {t("crm.quote.clone")}
                      </Button>
                      {quote.order_id && (
                        <Button
                          size="sm"
                          color="link"
                          className="px-0"
                          onClick={() => navigate(`/orders/${quote.order_id as number}`)}
                        >
                          {t("crm.quote.openOrder")}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {sendResult && (
                <div className="alert alert-info mt-3 mb-0">
                  <div className="mb-2">{t("crm.quote.sentHint")}</div>
                  <Input type="textarea" rows={3} readOnly value={sendResult.copy_text} />
                  <Button
                    size="sm"
                    color="dark"
                    className="mt-2"
                    onClick={() => {
                      void navigator.clipboard.writeText(sendResult.copy_text);
                      toast.success(t("crm.quote.copied"));
                    }}
                  >
                    {t("crm.quote.copyWa")}
                  </Button>
                </div>
              )}

              {acceptResult && (
                <div className="alert alert-success mt-3 mb-0">
                  <div className="fw-semibold mb-2">{t("crm.quote.acceptNext")}</div>
                  <ol className="small mb-3 ps-3">
                    <li>{t("crm.quote.acceptStep1")}</li>
                    <li>{t("crm.quote.acceptStep2")}</li>
                  </ol>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      color="dark"
                      onClick={() => navigate(`/orders/${acceptResult.orderId}`)}
                    >
                      {t("crm.quote.openOrder")}
                    </Button>
                    {acceptResult.clientId != null && (
                      <Button
                        size="sm"
                        color="light"
                        onClick={() =>
                          navigate(`/agenda?client_id=${acceptResult.clientId}`)
                        }
                      >
                        {t("crm.quote.scheduleAgenda")}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default CrmPage;
